import axios from 'axios';
import { API_URL } from '../constants/api';

// Types
export interface SubscriptionPlan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  targetAudience: string;
  priceMonthly: number;
  priceYearly: number;
  currency: string;
  features: string[];
  trialDays: number;
  stripePriceIdMonthly: string;
  stripePriceIdYearly: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface Subscription {
  id: string;
  subscriberId: string;
  subscriberType: string;
  planId: string;
  status: string;
  billingInterval: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialStart?: string;
  trialEnd?: string;
  cancelAtPeriodEnd: boolean;
  canceledAt?: string;
  activeFeatures: string[];
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionStats {
  trialCount: number;
  activeCount: number;
  expiredCount: number;
  canceledCount: number;
  totalRevenue: number;
  totalRevenueFormatted: string;
}

export interface GrantSubscriptionRequest {
  fieldId: string;
  planId: string;
  durationDays: number;
}

export interface GrantAllSubscriptionRequest {
  planId: string;
  durationDays: number;
}

export interface GrantResponse {
  message: string;
  subscriptionId?: string;
  periodEnd: string;
  created?: number;
  skipped?: number;
  totalFields?: number;
  errors?: string[];
}

// API Functions

// Subscriptions
export const getSubscriptions = async (
  status?: string,
  subscriberType?: string,
  page: number = 1,
  pageSize: number = 50
): Promise<Subscription[]> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (subscriberType) params.append('subscriberType', subscriberType);
  params.append('page', page.toString());
  params.append('pageSize', pageSize.toString());

  const response = await axios.get(`${API_URL}/api/admin/subscriptions?${params}`);
  return response.data;
};

export const getSubscription = async (id: string): Promise<{ subscription: Subscription; plan: SubscriptionPlan | null }> => {
  const response = await axios.get(`${API_URL}/api/admin/subscriptions/${id}`);
  return response.data;
};

export const getSubscriptionStats = async (): Promise<SubscriptionStats> => {
  const response = await axios.get(`${API_URL}/api/admin/subscriptions/stats`);
  return response.data;
};

export const extendSubscription = async (id: string, days: number): Promise<{ message: string }> => {
  const response = await axios.post(`${API_URL}/api/admin/subscriptions/${id}/extend`, { days });
  return response.data;
};

export const cancelSubscription = async (id: string, atPeriodEnd: boolean = false): Promise<{ message: string }> => {
  const response = await axios.post(`${API_URL}/api/admin/subscriptions/${id}/cancel`, { atPeriodEnd });
  return response.data;
};

export const activateSubscription = async (id: string, billingInterval: string, durationDays: number): Promise<{ message: string }> => {
  const response = await axios.post(`${API_URL}/api/admin/subscriptions/${id}/activate`, { billingInterval, durationDays });
  return response.data;
};

export const grantSubscription = async (request: GrantSubscriptionRequest): Promise<GrantResponse> => {
  const response = await axios.post(`${API_URL}/api/admin/subscriptions/grant`, request);
  return response.data;
};

export const grantSubscriptionToAll = async (request: GrantAllSubscriptionRequest): Promise<GrantResponse> => {
  const response = await axios.post(`${API_URL}/api/admin/subscriptions/grant-all`, request);
  return response.data;
};

// Plans
export const getPlans = async (targetAudience?: string, includeInactive: boolean = true): Promise<SubscriptionPlan[]> => {
  const params = new URLSearchParams();
  if (targetAudience) params.append('targetAudience', targetAudience);
  params.append('includeInactive', includeInactive.toString());

  const response = await axios.get(`${API_URL}/api/admin/subscriptions/plans?${params}`);
  return response.data;
};

export const getPlan = async (id: string): Promise<SubscriptionPlan> => {
  const response = await axios.get(`${API_URL}/api/admin/subscriptions/plans/${id}`);
  return response.data;
};

export const createPlan = async (plan: Partial<SubscriptionPlan>): Promise<{ message: string; plan: SubscriptionPlan }> => {
  const response = await axios.post(`${API_URL}/api/admin/subscriptions/plans`, plan);
  return response.data;
};

export const updatePlan = async (id: string, plan: Partial<SubscriptionPlan>): Promise<{ message: string }> => {
  const response = await axios.put(`${API_URL}/api/admin/subscriptions/plans/${id}`, plan);
  return response.data;
};

export const deletePlan = async (id: string, hardDelete: boolean = false): Promise<{ message: string }> => {
  const response = await axios.delete(`${API_URL}/api/admin/subscriptions/plans/${id}?hardDelete=${hardDelete}`);
  return response.data;
};

// Fields (for selection)
export const getFields = async (): Promise<{ id: string; name: string; email: string }[]> => {
  const response = await axios.get(`${API_URL}/api/admin/fields`);
  return response.data;
};
